import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Buildings,
  MagnifyingGlass,
  Users,
  WarningCircle,
  Funnel
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getSuperAdminData, Organization } from '../lib/api';

interface OrgStats {
  users: number;
  properties: number;
  apartments: number;
  totalIssues: number;
  openIssues: number;
}

export default function OrganizationsPage() {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgStats, setOrgStats] = useState<Record<string, OrgStats>>({});

  // Fetch data once on mount
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        // Single batch request for ALL data!
        const { organizations: orgs, users, properties, issues, apartments } = await getSuperAdminData();
        
        // Calculate stats per organization
        const stats: Record<string, OrgStats> = {};
        orgs.forEach(org => {
          const orgId = org.id;
          // Only count users that have this specific organization_id
          // Users without organization_id or with different org_id are excluded
          stats[orgId] = {
            users: users.filter(u => u.organization_id === orgId).length,
            properties: properties.filter(p => p.organization_id === orgId).length,
            apartments: apartments.filter(a => a.organization_id === orgId).length,
            totalIssues: issues.filter(i => i.organization_id === orgId).length,
            openIssues: issues.filter(i => i.organization_id === orgId && (i.status === 'open' || i.status === 'in_progress')).length
          };
        });
        setOrgStats(stats);
        setAllOrgs(orgs);
        setOrganizations(orgs);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter locally (instant, no API calls)
  useEffect(() => {
    let filtered = [...allOrgs];
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(org => 
        org.name?.toLowerCase().includes(searchLower) ||
        org.org_number?.toLowerCase().includes(searchLower)
      );
    }
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(org => org.status === statusFilter);
    }
    setOrganizations(filtered);
  }, [search, statusFilter, allOrgs]);

  const getStatusBadge = (status: string | undefined): string => {
    const badges: Record<string, string> = {
      active: 'badge-success',
      trial: 'badge-warning',
      suspended: 'badge-emergency',
      cancelled: 'badge-neutral'
    };
    return badges[status || ''] || 'badge-neutral';
  };

  const getPlanBadge = (plan: string | undefined): string => {
    const badges: Record<string, string> = {
      basic: 'bg-slate-100 text-slate-700',
      standard: 'bg-blue-100 text-blue-700',
      premium: 'bg-amber-100 text-amber-700'
    };
    return badges[plan || ''] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="organizations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Föreningar
        </h1>
        <Link to="/superadmin/organizations/new">
          <Button className="btn-press" data-testid="new-org-button">
            <Plus size={18} className="mr-2" />
            Ny förening
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="card-flat">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök namn, org.nummer..."
              className="pl-10"
              data-testid="org-search-input"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter">
              <Funnel size={16} className="mr-2 text-slate-400" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="suspended">Avstängd</SelectItem>
              <SelectItem value="cancelled">Avslutad</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="card-flat animate-pulse">
              <div className="h-5 w-48 bg-slate-200 rounded mb-2" />
              <div className="h-4 w-64 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      ) : organizations.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state">
            <Buildings size={48} className="empty-state-icon" />
            <p className="text-slate-500">Inga föreningar hittades</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Förening</th>
                <th>Org.nummer</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Användare</th>
                <th>Lägenheter</th>
                <th>Ärenden</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <tr key={org.id} data-testid={`org-row-${org.id}`}>
                  <td>
                    <Link 
                      to={`/superadmin/organizations/${org.id}`}
                      className="font-medium text-slate-900 hover:text-blue-600"
                    >
                      {org.name}
                    </Link>
                  </td>
                  <td className="font-mono text-slate-600">{org.org_number || '-'}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-md text-xs font-medium ${getPlanBadge(org.subscription_plan)}`}>
                      {org.subscription_plan}
                    </span>
                  </td>
                  <td>
                    <span className={getStatusBadge(org.subscription_status)}>
                      {org.subscription_status}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Users size={14} />
                      {orgStats[org.id]?.users || 0}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Buildings size={14} />
                      {orgStats[org.id]?.apartments || 0}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-slate-600">
                      <WarningCircle size={14} />
                      {orgStats[org.id]?.openIssues || 0} / {orgStats[org.id]?.totalIssues || 0}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
